import { z } from 'zod';

/**
 * One allowlisted SMA field that can be copied from the warehouse member feed.
 */
export interface StandardMemberAttributeBackfillColumnDto {
  camelCase: string;
  snakeCase: string;
  sqlType: 'varchar' | 'boolean';
}

/** Lifecycle values stored on an SMA column backfill job. */
export const SmaColumnBackfillJobStatusSchema = z.enum([
  'running',
  'cancel_requested',
  'cancelled',
  'completed',
  'failed',
]);

/** One sampled member from a backfill page. */
export interface SmaColumnBackfillJobSampleEntryDto {
  userId: string;
  clientId: number | null;
  clientUserId: string | null;
  outcome:
    | 'write'
    | 'missing_warehouse'
    | 'skipped_null'
    | 'skipped_delete'
    | 'missing_identity';
  value: string | null;
}

/**
 * Body for `POST …/member-standard-attributes/backfill-column/jobs`.
 *
 * Omitting `commit` (or any value other than `true`) is a dry run. A dry run
 * defaults to one page and cannot exceed `maxPages` (cap 5). `maxPages` is
 * rejected when `commit` is true.
 */
export const StartSmaColumnBackfillJobRequestSchema = z
  .object({
    column: z.string().min(1),
    commit: z.boolean().optional(),
    pageSize: z.number().int().positive().max(300).optional(),
    maxPages: z.number().int().positive().max(5).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.commit === true && value.maxPages != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'maxPages is only allowed on a dry run',
        path: ['maxPages'],
      });
    }
  });

/** Parsed operator request to start an SMA column backfill job. */
export type StartSmaColumnBackfillJobRequestDto = z.infer<
  typeof StartSmaColumnBackfillJobRequestSchema
>;

/**
 * Immediate response from starting a backfill job. The member walk continues
 * after this payload is returned.
 */
export interface StartSmaColumnBackfillJobResultDto {
  ok: true;
  jobId: string;
  status: 'running';
  column: string;
  commit: boolean;
  pageSize: number;
  /** Null when the job will walk every remaining SMA user. */
  maxPages: number | null;
  afterUserId: null;
}

/** Current backfill job, including dry-run sample rows. */
export interface SmaColumnBackfillJobDto {
  jobId: string;
  status: z.infer<typeof SmaColumnBackfillJobStatusSchema>;
  column: string;
  commit: boolean;
  pageSize: number;
  maxPages: number | null;
  afterUserId: string | null;
  pagesProcessed: number;
  uniqueMembers: number;
  matchedUsers: number;
  missingUsers: number;
  missingSma: number;
  skippedLatestNull: number;
  skippedLatestDelete: number;
  warehouseRowsScanned: number;
  updated: number;
  sample: SmaColumnBackfillJobSampleEntryDto[];
  lastError: string | null;
}
